import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { AlertTriangle, ClipboardList, Eye, FileWarning, Target, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Le registre melange chaines et objets {cle, libelle} : on affiche toujours du texte. */
const txt = (v) =>
  typeof v === "string" ? v : v?.libelle || v?.risque || v?.piece || v?.cle || "";

/**
 * DossierPanel — registre du dossier toujours visible a droite sur grand ecran.
 * Ce que la plateforme a retenu : hypotheses, criteres de l'eleve, risques, pieces manquantes.
 */
export default function DossierPanel({ conversationId, refreshKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const charger = useCallback(async () => {
    if (!conversationId) {
      setData(null);
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem("odys_token");
      const r = await axios.get(`${API}/chat/dossier/${conversationId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(r.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [conversationId]);

  useEffect(() => { charger(); }, [charger, refreshKey]);

  const vide =
    !data ||
    (!data.hypotheses?.length &&
      !data.criteres_eleve?.length &&
      !data.risques?.length &&
      !data.pieces_manquantes?.length);

  return (
    <aside className="dp-root" data-testid="dossier-panel">
      <div className="dp-head">
        <span className="dp-eyebrow"><ClipboardList className="w-3.5 h-3.5" /> Registre du dossier</span>
        <button type="button" onClick={charger} data-testid="dossier-refresh-btn" aria-label="Rafraichir">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "dp-spin" : ""}`} />
        </button>
      </div>

      {vide ? (
        <p className="dp-empty" data-testid="dossier-panel-empty">
          Ce que la plateforme retient de ton dossier apparaitra ici : chiffres retenus, tes criteres,
          risques non chiffres et pieces manquantes.
        </p>
      ) : (
        <div className="dp-body">
          {!!data.hypotheses?.length && (
            <section data-testid="dossier-hypotheses">
              <h4><Target className="w-3.5 h-3.5" /> Chiffres retenus</h4>
              <ul>
                {data.hypotheses.slice(0, 12).map((h, i) => (
                  <li key={i}>
                    <span className="dp-k">{String(h.cle || "").replace(/_/g, " ")}</span>
                    <span className="dp-v">{txt(h.valeur) || h.valeur}</span>
                    {h.source && <span className="dp-src">{h.source}</span>}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {!!data.criteres_eleve?.length && (
            <section data-testid="dossier-criteres">
              <h4><ClipboardList className="w-3.5 h-3.5" /> Tes criteres</h4>
              <ul>{data.criteres_eleve.slice(0, 8).map((c, i) => <li key={i}>{txt(c)}</li>)}</ul>
            </section>
          )}

          {!!data.risques?.length && (
            <section className="dp-warn" data-testid="dossier-risques">
              <h4><AlertTriangle className="w-3.5 h-3.5" /> Risques non chiffres</h4>
              <ul>
                {data.risques.slice(0, 8).map((r, i) => (
                  <li key={i}>
                    {txt(r)}
                    {r?.fourchette && <span className="dp-src">fourchette : {r.fourchette}</span>}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {!!data.pieces_manquantes?.length && (
            <section className="dp-miss" data-testid="dossier-pieces">
              <h4><FileWarning className="w-3.5 h-3.5" /> Pieces manquantes</h4>
              <ul>{data.pieces_manquantes.slice(0, 10).map((p, i) => <li key={i}>{txt(p)}</li>)}</ul>
            </section>
          )}

          {data.non_visite && (
            <p className="dp-flag" data-testid="dossier-non-visite">
              <Eye className="w-3.5 h-3.5" /> Bien non visite : tout reste hypothetique
            </p>
          )}
        </div>
      )}

      <style>{`
        .dp-root {
          width: 22rem; flex: none; border-left: 1px solid var(--line, #e2e8f0);
          background: var(--paper-2, #f6f2ea); display: flex; flex-direction: column;
          overflow-y: auto; font-family: var(--sans);
        }
        .dp-head {
          display: flex; align-items: center; justify-content: space-between;
          padding: 0.85rem 1rem; border-bottom: 1px solid var(--line, #e2e8f0);
          position: sticky; top: 0; background: var(--paper-2, #f6f2ea); z-index: 2;
        }
        .dp-eyebrow {
          display: inline-flex; align-items: center; gap: 0.4rem;
          font-size: 0.64rem; letter-spacing: 0.14em; text-transform: uppercase;
          font-weight: 600; color: var(--gold-deep, #8c6635);
        }
        .dp-head button { color: #94a3b8; padding: 0.25rem; border-radius: 6px; }
        .dp-head button:hover { color: var(--ink, #16233b); background: rgba(0,0,0,0.04); }
        .dp-spin { animation: dp-rot 0.9s linear infinite; }
        @keyframes dp-rot { to { transform: rotate(360deg); } }
        .dp-empty { padding: 1rem; font-size: 0.78rem; line-height: 1.55; color: #7b8794; }
        .dp-body { padding: 0.9rem 1rem 1.6rem; display: flex; flex-direction: column; gap: 1.1rem; }
        .dp-body h4 {
          display: flex; align-items: center; gap: 0.4rem;
          font-size: 0.7rem; letter-spacing: 0.08em; text-transform: uppercase;
          color: var(--ink, #16233b); margin-bottom: 0.5rem; font-weight: 700;
        }
        .dp-body ul { display: flex; flex-direction: column; gap: 0.4rem; }
        .dp-body li {
          font-size: 0.78rem; line-height: 1.45; color: #37465e;
          padding-left: 0.6rem; border-left: 2px solid var(--line, #e2e8f0);
        }
        .dp-k { display: block; font-size: 0.68rem; color: #7b8794; }
        .dp-v { font-weight: 600; color: var(--ink, #16233b); font-variant-numeric: tabular-nums; }
        .dp-src { display: block; font-size: 0.64rem; color: #9aa5b1; font-style: italic; }
        .dp-warn li { border-left-color: #d9822b; }
        .dp-miss li { border-left-color: #1f6f6b; }
        .dp-flag {
          display: flex; align-items: center; gap: 0.4rem; font-size: 0.72rem;
          color: #8c6635; background: rgba(181,137,74,0.12);
          padding: 0.5rem 0.6rem; border-radius: 8px;
        }
        @media (max-width: 1279px) { .dp-root { display: none; } }
      `}</style>
    </aside>
  );
}
